import React, { useEffect, useContext, useCallback, useRef } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { TabNodeContext } from '../../../providers/TabNodeProvider';

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      width: 254,
      border: 0,
      alignItems: 'center',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      display: 'flex',
      height: 28,
      justifyContent: 'center',
      position: 'absolute',
      textDecoration: 'underline',
      top: 0,
      left: 8,
      zIndex: -1,
      userSelect: 'none',
      opacity: 0,
      '&:hover': {
        cursor: 'pointer'
      },
      '&:focus': {
        opacity: 1,
        zIndex: 0
      }
    },
    drawerOpen: {},
    drawerClosed: {}
  };
});

export interface TabSkipButtonProps {
  open: boolean;
}

const TabSkipButton = (props: TabSkipButtonProps): React.ReactElement => {
  const { open } = props;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const classes = useStyles();
  const { t } = useTranslation('TabSkipButton');
  const BUTTON_TEXT = 'Skip to main content';

  const { node } = useContext(TabNodeContext);

  const handleClick = useCallback(() => {
    if (node) node.focus();
  }, [node]);

  const keydownListener = useCallback(
    (event: KeyboardEvent) => {
      if (!(event.target instanceof HTMLButtonElement)) return;
      const { innerText } = event.target as HTMLButtonElement;
      if (innerText !== BUTTON_TEXT) return;
      if (event.key === 'Enter' || event.key === ' ') handleClick();
    },
    [handleClick]
  );

  useEffect(() => {
    if (!buttonRef.current) return;
    window.addEventListener('keydown', keydownListener);
    return (): void => {
      window.removeEventListener('keydown', keydownListener);
    };
  }, [handleClick, keydownListener]);

  return (
    <button
      ref={buttonRef}
      className={clsx(classes.container, {
        [classes.drawerOpen]: open,
        [classes.drawerClosed]: !open
      })}
      tabIndex={0}
      onClick={handleClick}
    >
      <Typography>{t(BUTTON_TEXT)}</Typography>
    </button>
  );
};

export default TabSkipButton;
